/* eslint-disable no-param-reassign */
import * as React from 'react';

function useMemo<Value, Condition = any[]>(
	getValue: () => Value,
	condition: Condition,
	shouldUpdate: (prev: Condition, next: Condition) => boolean,
) {
	const cacheRef = React.useRef<any>({});

	if (!('value' in cacheRef.current) || shouldUpdate(cacheRef.current.condition, condition)) {
		cacheRef.current.value = getValue();
		cacheRef.current.condition = condition;
	}

	return cacheRef.current.value;
}

function fillRef<T>(ref: React.Ref<T>, node: T) {
	if (typeof ref === 'function') {
		ref(node);
	} else if (typeof ref === 'object' && ref && 'current' in ref) {
		(ref as any).current = node;
	}
}

function composeRef<T>(...refs: React.Ref<T>[]): React.Ref<T> {
	const refList = refs.filter((ref) => ref);
	if (refList.length <= 1) {
		return refList[0];
	}

	return (node: T) => {
		refs.forEach((ref) => {
			fillRef(ref, node);
		});
	};
}

export function useComposeRef<T>(...refs: React.Ref<T>[]): React.Ref<T> {
	return useMemo(
		() => composeRef(...refs),
		refs,
		(prev, next) => prev.length === next.length && prev.every((ref, i) => ref === next[i]),
	);
}
